import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import RestoreIcon from '@mui/icons-material/Restore';

interface ArchivedClient {
  _id: string;
  companyName: string;
  ownerName: string;
  email?: string;
  phoneNumber?: string;
  archivedDate?: string;
}

const ArchivedClients: React.FC = () => {
  const [clients, setClients] = useState<ArchivedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchivedClients();
  }, []);

  const fetchArchivedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/clients/archived');
      const clientData = Array.isArray(response.data) ? response.data : [];
      setClients(clientData);
    } catch (err: any) {
      
      const errorMessage = err?.response?.data?.error || 'Failed to fetch archived clients';
      setError(errorMessage);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    return clients.filter(client =>
      searchQuery === '' ||
      (client.companyName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (client.ownerName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (client.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
  }, [clients, searchQuery]);

  const handleRestore = async (clientId: string) => {
    try {
      await api.post(`/api/clients/${clientId}/reactivate`);
      fetchArchivedClients();
    } catch (err) {
      
      setError('Failed to reactivate client');
    }
  };

  if (loading) return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px'
      }}
    >
      <Box 
        sx={{ 
          animation: 'spin 1s linear infinite',
          '@keyframes spin': {
            '0%': {
              transform: 'rotate(0deg)',
            },
            '100%': {
              transform: 'rotate(360deg)',
            },
          },
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          borderBottom: '2px solid #90caf9',
          mx: 'auto',
          mb: 2
        }}
      />
    </Box>
  );
  
  if (error) return (
    <Typography 
      color="error.light" 
      sx={{ 
        p: 3, 
        backgroundColor: 'rgba(211, 47, 47, 0.15)', 
        borderRadius: 1,
        color: '#ff8a80'
      }}
    >
      {error}
    </Typography>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto', px: 4, py: 8 }}>
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            color: 'white',
            fontFamily: 'Inter, Roboto, sans-serif',
            fontWeight: 700
          }}
        >
          Archived Clients
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by company name, owner, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiFormHelperText-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          }}
        />
      </Box>

      <TableContainer 
        component={Paper}
        sx={{ 
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& th': { 
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 600,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }
            }}>
              <TableCell>Company Name</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Archived Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& tr': { borderBottom: '1px solid rgba(255, 255, 255, 0.1)' } }}>
            {filteredClients.map((client) => (
              <TableRow 
                key={client._id}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                  '& td': { color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }
                }}
              >
                <TableCell>
                  <Typography variant="body1" sx={{ color: 'white' }}>
                    {client.companyName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: 'white' }}>{client.ownerName}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'white' }}>{client.phoneNumber}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {client.email}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: 'white' }}>
                  {client.archivedDate ? new Date(client.archivedDate).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleRestore(client._id)}
                    color="primary"
                    size="small"
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    <RestoreIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ArchivedClients;
